/**
 * Form Design Template Library — create, edit, clone, and set the default
 * reusable visual theme(s) a Smart Form can use. Each template is org-scoped
 * (form_design_templates).
 *
 * Layout is stacked in rows: a template chooser, then the editor grouped into
 * labeled row-sections, then a live preview that can toggle between the PUBLIC
 * form look (branded header, submit button, background) and the INTERNAL look
 * (the neutral in-app renderer) — so authors see both surfaces a design affects.
 *
 * Sits under Settings → Form Design. The form builder's Design selector picks
 * from these; the public renderer resolves the chosen (or default) template.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Copy, Trash2, Star, Loader2, Palette, DownloadCloud, Upload, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { FormRenderer } from '@/components/forms/FormRenderer';
import { FormThemeControls } from '@/components/forms/FormThemeControls';
import {
  DEFAULT_FORM_THEME,
  mergeFormTheme,
  themeButtonStyle,
  themePageBackgroundStyle,
  BACKGROUND_PRESETS,
  type FormTheme,
  type PartialFormTheme,
} from '@/components/forms/formTheme';
import formDesignTemplatesApi, { type FormDesignTemplate } from '@/services/api/formDesignTemplates';
import type { FormSectionSchema } from '@/services/api/forms';

// A small representative form so authors can judge colors/spacing/typography.
const SAMPLE_SECTIONS: FormSectionSchema[] = [
  {
    section_id: 's1',
    title: 'Contact information',
    description: 'A preview of how your branded form will look.',
    order: 0,
    collapsible: false,
    collapsed_by_default: false,
    fields: [
      { field_id: 'full_name', field_type: 'text', label: 'Full name', required: true, order: 0 } as any,
      { field_id: 'email', field_type: 'text', label: 'Email address', placeholder: 'you@example.com', order: 1 } as any,
      {
        field_id: 'reason',
        field_type: 'select',
        label: 'How can we help?',
        options: ['General question', 'Enroll in a program', 'Request a callback'],
        order: 2,
      } as any,
      { field_id: 'notes', field_type: 'textarea', label: 'Anything else?', order: 3 } as any,
      {
        field_id: 'consent',
        field_type: 'checkbox',
        label: 'Consent',
        placeholder: 'I agree to be contacted',
        order: 4,
      } as any,
    ],
  },
];

interface DraftState {
  id: number | null;
  name: string;
  description: string;
  is_default: boolean;
  theme: FormTheme; // fully merged for editing convenience
}

function toDraft(t: FormDesignTemplate | null): DraftState {
  return {
    id: t?.id ?? null,
    name: t?.name ?? 'New design',
    description: t?.description ?? '',
    is_default: t?.is_default ?? false,
    theme: mergeFormTheme(t?.theme),
  };
}

/** A faithful mini-render of the PUBLIC form (header + branded submit + bg). */
function PublicPreview({ theme }: { theme: FormTheme }) {
  const [values, setValues] = useState<Record<string, any>>({});
  return (
    <div className="rounded-lg overflow-hidden border dark:border-gray-700">
      <div className="p-6" style={themePageBackgroundStyle(theme)}>
        <div className="mx-auto" style={{ maxWidth: 520 }}>
          <div className="text-center mb-4">
            {theme.branding.show_logo && theme.branding.logo_url && (
              <img src={theme.branding.logo_url} alt="" className="h-10 mx-auto mb-2" />
            )}
            <h1 className="text-xl font-bold" style={{ color: theme.colors.text, fontFamily: theme.typography.heading_font || theme.typography.font_family }}>
              Doula Services Interest
            </h1>
          </div>
          <div className="rounded-xl shadow-sm p-5 space-y-4" style={{ backgroundColor: theme.colors.surface }}>
            <FormRenderer sections={SAMPLE_SECTIONS} theme={theme} value={values} onChange={(id, v) => setValues((p) => ({ ...p, [id]: v }))} />
            <button className="w-full py-2.5 text-sm font-medium rounded-lg" style={themeButtonStyle(theme)}>
              Submit
            </button>
          </div>
          {theme.branding.show_powered_by && (
            <p className="text-xs text-center mt-3" style={{ color: theme.colors.muted }}>Powered by WeaveIn.ai</p>
          )}
        </div>
      </div>
    </div>
  );
}

/** The INTERNAL in-app render (neutral chrome, themed fields). */
function InternalPreview({ theme }: { theme: FormTheme }) {
  const [values, setValues] = useState<Record<string, any>>({});
  return (
    <div className="rounded-lg border dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
      <FormRenderer sections={SAMPLE_SECTIONS} theme={theme} value={values} onChange={(id, v) => setValues((p) => ({ ...p, [id]: v }))} />
    </div>
  );
}

export default function FormDesignTemplateManager() {
  const [templates, setTemplates] = useState<FormDesignTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<DraftState>(toDraft(null));
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [uploading, setUploading] = useState<'logo' | 'bg' | null>(null);
  const [previewMode, setPreviewMode] = useState<'public' | 'internal'>('public');
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bgInputRef = useRef<HTMLInputElement>(null);

  const flashInfo = (msg: string) => {
    setInfo(msg);
    setError(null);
    window.setTimeout(() => setInfo(null), 3500);
  };

  const load = async (selectId?: number | null) => {
    setLoading(true);
    try {
      const list = await formDesignTemplatesApi.list();
      setTemplates(list);
      if (selectId) {
        const found = list.find((d) => d.id === selectId);
        if (found) setDraft(toDraft(found));
      } else if (draft.id == null && list.length > 0) {
        setDraft(toDraft(list.find((d) => d.is_default) || list[0]));
      }
    } catch {
      setError('Failed to load design templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewTheme: FormTheme = useMemo(() => draft.theme, [draft.theme]);

  const setBranding = (key: keyof FormTheme['branding'], value: any) =>
    setDraft((d) => ({ ...d, theme: { ...d.theme, branding: { ...d.theme.branding, [key]: value } } }));
  const setBackground = (patch: Partial<FormTheme['background']>) =>
    setDraft((d) => ({ ...d, theme: { ...d.theme, background: { ...d.theme.background, ...patch } } }));

  const startNew = () => {
    setDraft(toDraft(null));
    setError(null);
    setInfo(null);
  };

  const startClone = (t: FormDesignTemplate) =>
    setDraft({ ...toDraft(t), id: null, name: `${t.name} (copy)`, is_default: false });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: draft.name,
        description: draft.description,
        theme: draft.theme,
        is_default: draft.is_default,
      };
      const saved = draft.id
        ? await formDesignTemplatesApi.update(draft.id, payload)
        : await formDesignTemplatesApi.create(payload);
      await load(saved.id);
      flashInfo('Design saved.');
    } catch {
      setError('Failed to save. Check your inputs and try again.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t: FormDesignTemplate) => {
    if (!window.confirm(`Delete "${t.name}"? Forms using it fall back to your default.`)) return;
    try {
      await formDesignTemplatesApi.remove(t.id);
      if (draft.id === t.id) startNew();
      await load();
    } catch {
      setError('Failed to delete');
    }
  };

  const makeDefault = async (t: FormDesignTemplate) => {
    try {
      await formDesignTemplatesApi.setDefault(t.id);
      await load(t.id);
      flashInfo('Set as organization default.');
    } catch {
      setError('Failed to set default');
    }
  };

  const importBrand = async () => {
    setImporting(true);
    setError(null);
    try {
      const { resolved_theme, theme } = await formDesignTemplatesApi.importFromBranding();
      const hasAny = theme && Object.keys(theme).length > 0;
      setDraft((d) => ({ ...d, theme: mergeFormTheme(resolved_theme) }));
      flashInfo(
        hasAny
          ? 'Imported colors, fonts & logo from your brand settings.'
          : 'No brand settings found — applied the default palette. Set brand colors in Settings → Branding first.'
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not import brand settings.');
    } finally {
      setImporting(false);
    }
  };

  const onPickImage = async (kind: 'logo' | 'bg', file?: File | null) => {
    if (!file) return;
    setUploading(kind);
    setError(null);
    try {
      const { url } = await formDesignTemplatesApi.uploadAsset(file);
      if (kind === 'logo') {
        setBranding('logo_url', url);
        setBranding('show_logo', true);
      } else {
        setBackground({ type: 'image', value: url });
      }
      flashInfo(kind === 'logo' ? 'Logo uploaded.' : 'Background image uploaded.');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(null);
    }
  };

  const activeBg = draft.theme.background;
  const bgIsPresetSolid = activeBg.type === 'solid';

  return (
    <div className="space-y-5 max-w-5xl">
      <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPickImage('logo', e.target.files?.[0])} />
      <input ref={bgInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPickImage('bg', e.target.files?.[0])} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Palette className="w-5 h-5" /> Form Design
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Reusable themes your forms and public links can use. The default applies to any form that
            hasn’t picked one.
          </p>
        </div>
        <Button onClick={startNew} variant="outline">
          <Plus className="w-4 h-4 mr-2" /> New design
        </Button>
      </div>

      {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-200">{error}</div>}
      {info && (
        <div className="p-3 bg-green-50 text-green-700 text-sm rounded-lg border border-green-200 flex items-center gap-2">
          <Check className="w-4 h-4" /> {info}
        </div>
      )}

      {/* Row 1 — template chooser (horizontal) */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {loading ? (
          <div className="text-sm text-gray-400 flex items-center gap-2 py-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          templates.map((t) => (
            <button
              key={t.id}
              onClick={() => setDraft(toDraft(t))}
              className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
                draft.id === t.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <span className="w-4 h-4 rounded-full border shrink-0" style={{ backgroundColor: mergeFormTheme(t.theme).colors.primary }} />
              <span className="truncate max-w-[160px] text-gray-800 dark:text-gray-200">{t.name}</span>
              {t.is_default && <Star className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
            </button>
          ))
        )}
      </div>

      {/* Row 2 — editor (stacked labeled sections) */}
      <div className="border rounded-lg p-5 space-y-6 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">{draft.id ? 'Edit design' : 'New design'}</h3>
          <Button size="sm" variant="ghost" onClick={importBrand} disabled={importing}>
            {importing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <DownloadCloud className="w-4 h-4 mr-1.5" />}
            Import brand
          </Button>
        </div>

        {/* Basics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Name</Label>
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </div>
          <div>
            <Label>Description</Label>
            <Input
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="e.g. Marketing blue — outreach campaigns"
            />
          </div>
        </div>
        <div className="flex items-center justify-between max-w-md">
          <Label className="cursor-pointer">Set as organization default</Label>
          <Switch checked={draft.is_default} onCheckedChange={(v: boolean) => setDraft({ ...draft, is_default: v })} />
        </div>

        {/* Colours, typography and control shape now live in one shared
            component, so the form builder edits a theme with exactly the same
            controls as this screen. Logo and background stay here: both carry
            asset upload and are brand setup done once, not per form. */}
        <FormThemeControls
          theme={draft.theme}
          onChange={(theme) => setDraft((d) => ({ ...d, theme }))}
        />

        {/* Logo */}
        <section>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Logo</h4>
          <div className="flex items-center gap-3 flex-wrap">
            {draft.theme.branding.logo_url ? (
              <img src={draft.theme.branding.logo_url} alt="logo" className="h-10 rounded border bg-white p-1" />
            ) : (
              <div className="h-10 w-24 rounded border border-dashed flex items-center justify-center text-[11px] text-gray-400">No logo</div>
            )}
            <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} disabled={uploading === 'logo'}>
              {uploading === 'logo' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              Upload logo
            </Button>
            {draft.theme.branding.logo_url && (
              <Button variant="ghost" size="sm" onClick={() => setBranding('logo_url', '')}>Remove</Button>
            )}
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 ml-2">
              <Switch checked={draft.theme.branding.show_logo} onCheckedChange={(v: boolean) => setBranding('show_logo', v)} />
              Show logo
            </label>
          </div>
          <div className="mt-2 max-w-lg">
            <Label className="text-xs text-gray-400">…or paste a logo URL</Label>
            <Input value={draft.theme.branding.logo_url} onChange={(e) => setBranding('logo_url', e.target.value)} placeholder="https://…/logo.png" />
          </div>
        </section>

        {/* Background */}
        <section>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Background (public form)</h4>
          <div className="flex flex-wrap gap-2">
            {BACKGROUND_PRESETS.map((p) => {
              const selected =
                (p.id === 'none' && bgIsPresetSolid) ||
                (p.id !== 'none' && activeBg.type === p.type && activeBg.value === p.value);
              return (
                <button
                  key={p.id}
                  title={p.label}
                  onClick={() => setBackground(p.id === 'none' ? { type: 'solid', value: '' } : { type: p.type, value: p.value })}
                  className={`w-16 h-12 rounded-md border-2 relative overflow-hidden ${selected ? 'border-blue-500' : 'border-transparent ring-1 ring-gray-200 dark:ring-gray-700'}`}
                  style={p.id === 'none' ? { background: draft.theme.colors.background } : { background: p.swatch }}
                >
                  {selected && <Check className="w-4 h-4 text-blue-600 absolute top-0.5 right-0.5 bg-white/80 rounded" />}
                  <span className="absolute bottom-0 inset-x-0 text-[9px] text-center bg-black/30 text-white">{p.label}</span>
                </button>
              );
            })}
            <button
              onClick={() => bgInputRef.current?.click()}
              className="w-16 h-12 rounded-md border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center text-[9px] text-gray-500"
              disabled={uploading === 'bg'}
            >
              {uploading === 'bg' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload
            </button>
          </div>
          {activeBg.type === 'image' && /^https?:\/\//.test(activeBg.value) && (
            <p className="text-xs text-gray-400 mt-1 truncate max-w-lg">Image: {activeBg.value}</p>
          )}
        </section>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap pt-3 border-t dark:border-gray-700">
          <Button onClick={save} disabled={saving || !draft.name.trim()}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {draft.id ? 'Save changes' : 'Create design'}
          </Button>
          {draft.id ? (
            <>
              <Button variant="outline" onClick={() => startClone(templates.find((t) => t.id === draft.id)!)}>
                <Copy className="w-4 h-4 mr-1.5" /> Clone
              </Button>
              {!draft.is_default && (
                <Button variant="outline" onClick={() => makeDefault(templates.find((t) => t.id === draft.id)!)}>
                  <Star className="w-4 h-4 mr-1.5" /> Make default
                </Button>
              )}
              <Button variant="ghost" className="text-red-600" onClick={() => remove(templates.find((t) => t.id === draft.id)!)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          ) : null}
          <Button variant="ghost" onClick={() => setDraft((d) => ({ ...d, theme: { ...DEFAULT_FORM_THEME } }))}>
            Reset styles
          </Button>
        </div>
      </div>

      {/* Row 3 — live preview (public / internal toggle) */}
      <div className="border rounded-lg p-4 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Live preview</div>
          <div className="inline-flex rounded-lg border dark:border-gray-700 overflow-hidden text-sm">
            {(['public', 'internal'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setPreviewMode(m)}
                className={`px-3 py-1.5 capitalize ${previewMode === m ? 'bg-blue-600 text-white' : 'bg-transparent text-gray-600 dark:text-gray-300'}`}
              >
                {m} form
              </button>
            ))}
          </div>
        </div>
        {previewMode === 'public' ? (
          <PublicPreview key="pub" theme={previewTheme} />
        ) : (
          <InternalPreview key="int" theme={previewTheme} />
        )}
      </div>
    </div>
  );
}
